/**
 * Types for procedure analysis sources. Source shapes must be kept in sync with backend shapes defined in
 * benchling/procedures/integrations/analysis/procedure_analysis_source_types.py.
 */
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {ParameterStructuredTableMode} from 'notebook/integrations/procedures/ParameterStructuredTableConstants';
import {ProcedureParameterType} from 'procedures/ProcedureDomainTypes';
import {ScrollableTableRow} from 'ui-library/scrollable/ScrollableTableConstants';

// Keep in sync with benchling/procedures/integrations/analysis/procedure_analysis_source_types.py
export const SOURCES_NOT_CONTAINED_IN_STEP_OR_DOCUMENT = 'sources_not_contained_in_step_or_document';

interface BaseSourceWithColumnDefs {
  columnDefs: Array<DataFrameColumnDef>;
}

type ConditionsResponse = BaseSourceWithColumnDefs;

export interface LabAutoConfigDatasetInfo extends BaseSourceWithColumnDefs {
  outputFileConfigName: string;
  outputFileConfigId: string;
}

export interface LabAutoExecutionDatasetInfo extends LabAutoConfigDatasetInfo {
  datasetId: string;
  datasetName: string;
  createdAt: string;
}

export function isLabAutoExecutionDatasetInfo(
  datasetInfo: LabAutoExecutionDatasetInfo | LabAutoConfigDatasetInfo
): datasetInfo is LabAutoExecutionDatasetInfo {
  return (datasetInfo as LabAutoExecutionDatasetInfo).datasetId != null;
}

interface LabAutoRunSource {
  runId: string;
  runSchemaId: string;
  schemaName: string;
  outputs: Array<LabAutoExecutionDatasetInfo>;
}

interface LabAutoRunSchemaSource {
  runSchemaId: string;
  schemaName: string;
  outputs: Array<LabAutoConfigDatasetInfo>;
}

interface BasePlateSourceWithColumnDefs {
  contentsColumnDefs: Array<DataFrameColumnDef>;
  positionsColumnDefs: Array<DataFrameColumnDef>;
}

export interface TestOrderSource extends BaseSourceWithColumnDefs {
  plateIds: Array<string>;
}

export interface PreviousTestOrderSource extends BaseSourceWithColumnDefs {
  studies: Array<StudyInfo>;
}

// Exported for testing
export interface StudyInfo {
  name: string;
  editUrl: string;
}

export interface PlateSource extends BasePlateSourceWithColumnDefs {
  plateId: string;
  plateName: string;
  plateSchemaId: string;
  plateSchemaName: string;
  plateColumnDefs: Array<DataFrameColumnDef>;
  hasDataParameterConfirmations: boolean;
  hasMaterialInputConfirmations: boolean;
}

interface BasePlateMapSource extends BasePlateSourceWithColumnDefs {
  id: string;
  name: string;
}

export interface PlateRecordSource extends BasePlateMapSource {
  plateId: string;
}

export interface PlateDesignSource extends BasePlateMapSource {
  plateDesignVersionId: string;
}

export type PlateRecordSpec = Pick<PlateRecordSource, 'id' | 'plateId' | 'name'>;
export type PlateDesignSpec = Pick<PlateDesignSource, 'id' | 'plateDesignVersionId'>;

export function isPlateDesignSource(
  plateMapSource: PlateRecordSource | PlateDesignSource
): plateMapSource is PlateDesignSource {
  return (plateMapSource as PlateDesignSource).plateDesignVersionId != null;
}

interface BaseTableSource extends BaseSourceWithColumnDefs {
  tableName: string;
  hasCreationContext: boolean;
}

export interface TableSourceWithSchema extends BaseTableSource {
  schemaId: string;
  schemaName: string;
}

export interface OfflineResultSchemaResponse extends BaseSourceWithColumnDefs {
  schemaId: string;
  schemaName: string;
}

interface ParameterTableSource extends BaseTableSource {
  mode: ParameterStructuredTableMode;
}

export interface AnalysisInputs {
  conditions: ConditionsResponse | null;
  laRuns: Array<LabAutoRunSource> | Array<LabAutoRunSchemaSource>;
  plates: Array<PlateSource>;
  plateRecords: Array<PlateRecordSource>;
  plateDesigns: Array<PlateDesignSource>;
  registrationTables: Array<TableSourceWithSchema>;
  resultTables: Array<TableSourceWithSchema>;
  parameterTables: Array<ParameterTableSource>;
  testOrders: TestOrderSource | null;
  previousTestOrders: PreviousTestOrderSource | null;
  offlineResultSchemas: Array<OfflineResultSchemaResponse>;
}

export interface DocumentInfo {
  id: string;
  editUrl: string;
  name: string;
}

export interface StepInfo {
  id: string;
  name: string;
}

export function isDocumentInfo(
  stepOrDocumentInfo: DocumentInfo | StepInfo
): stepOrDocumentInfo is DocumentInfo {
  return (stepOrDocumentInfo as DocumentInfo).editUrl != null;
}

export interface DocumentWithAnalysisSources {
  document: DocumentInfo;
  analysisInputs: AnalysisInputs;
}

export interface StepWithAnalysisSources {
  step: StepInfo;
  analysisInputs: AnalysisInputs;
}

export type StepOrDocumentAnalysisSources = DocumentWithAnalysisSources | StepWithAnalysisSources;

export function isDocumentAnalysisSource(
  stepOrDocSource: StepOrDocumentAnalysisSources
): stepOrDocSource is DocumentWithAnalysisSources {
  return (stepOrDocSource as DocumentWithAnalysisSources).document != null;
}

export function getStepOrDocumentFromSource(
  stepOrDocSource: StepOrDocumentAnalysisSources
): DocumentInfo | StepInfo {
  return isDocumentAnalysisSource(stepOrDocSource) ? stepOrDocSource.document : stepOrDocSource.step;
}

/** We use a sentinel document or step to includes sources that aren't specific to a single document or step. */
export function isSentinelDocumentOrStep(stepOrDocSource: StepOrDocumentAnalysisSources): boolean {
  return isDocumentAnalysisSource(stepOrDocSource)
    ? stepOrDocSource.document.id === SOURCES_NOT_CONTAINED_IN_STEP_OR_DOCUMENT
    : stepOrDocSource.step.id === SOURCES_NOT_CONTAINED_IN_STEP_OR_DOCUMENT;
}

export interface WorksheetAnalysisSources {
  sourcesPerStep: Array<DocumentWithAnalysisSources>;
  availableParameterTypes: Array<ProcedureParameterType>;
}

export interface MethodAnalysisSources {
  sourcesPerStep: Array<StepWithAnalysisSources>;
  availableParameterTypes: Array<ProcedureParameterType>;
}

export type AnalysisSourcesForConfigOrExecution = MethodAnalysisSources | WorksheetAnalysisSources;

// Keep in sync with benchling/procedures/integrations/analysis/seed_procedure_datasets_helpers.py
export enum PlateDatasetType {
  CONTENTS = 'CONTENTS',
  POSITIONS = 'POSITIONS',
}

export enum AnalysisSourcesOrigin {
  CONFIGURATION = 'CONFIGURATION',
  EXECUTION = 'EXECUTION',
}

export enum DatasetType {
  ENTITY_SCHEMA = 'ENTITY_SCHEMA',
  LAB_AUTO = 'LAB_AUTO',
  CONDITIONS = 'CONDITIONS',
  DATA_PARAMETER = 'DATA_PARAMETER',
  MATERIAL_INPUT = 'MATERIAL_INPUT',
  MATERIAL_OUTPUT = 'MATERIAL_OUTPUT',
  EQUIPMENT = 'EQUIPMENT',
  TEST_ORDERS = 'TEST_ORDERS',
  PREVIOUS_TEST_ORDERS = 'PREVIOUS_TEST_ORDERS',
  PLATE_SUMMARY = 'PLATE_SUMMARY',
  RESULT_SCHEMA = 'RESULT_SCHEMA',
  INDIVIDUAL_PLATE_POSITIONS = 'INDIVIDUAL_PLATE_POSITIONS',
  INDIVIDUAL_PLATE_CONTENTS = 'INDIVIDUAL_PLATE_CONTENTS',
  PLATE_RECORD_POSITIONS = 'PLATE_RECORD_POSITIONS',
  PLATE_RECORD_CONTENTS = 'PLATE_RECORD_CONTENTS',
  PLATE_DESIGN_POSITIONS = 'PLATE_DESIGN_POSITIONS',
  PLATE_DESIGN_CONTENTS = 'PLATE_DESIGN_CONTENTS',
}

export interface AnalysisSourceRowData {
  dataset: JSX.Element;
  dataSources: JSX.Element;
  creationTime: JSX.Element;
  steps: JSX.Element;
  usedInAnalysis?: JSX.Element;
  // If not defined, we'll use the default row height for ScrollableTable
  height?: number;
}

export interface AnalysisSourceRow extends ScrollableTableRow {
  data: AnalysisSourceRowData;
  extra: {datasetType: DatasetType};
}
